import React, {
  ChangeEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Box,
  CircularProgress,
  Grid,
  Modal,
  Pagination,
  Select,
  MenuItem,
  SelectChangeEvent,
  Typography,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useLocation } from 'react-router-dom';
import { useHotkeys } from 'react-hotkeys-hook';
import { isTypingContext } from '../ThreadsDetails/Cockpit/shortcutUtils';
import {
  addBulkOperation,
  bulkOperationKeyForThreat,
} from '../../store/bulk/operations/slice';
import FilterCard from './FilterCard';
import {
  getThreatApi,
  threatListQueryKey,
  useBulkChangeStatusMutation,
  useThreatListQuery,
  ThreatListParams,
} from '../../queries/threats';
import { useCurrentUserQuery } from '../../queries/user';
import {
  useCompanyDescendantIds,
  expandWithDescendants,
} from '../../queries/companyDescendants';
import {
  defaultEndDate,
  defaultReviewStatusFilter,
  defaultServiceFilter,
  defaultSortBy,
  defaultSortOrder,
  defaultSourcesFilter,
  defaultStartDate,
  defaultStatusFilter,
  getDateOrDefault,
  getLabelsAndIds,
  PER_PAGE_RECORDS,
  statusList,
  SourcesData,
  threatsStatus,
  EXTERNAL_THREAT_PENDING_STATUS,
  isRejectionStatus,
} from '../../utils';
import {
  ThreatCardWrapper,
  ThreatsCountLabel,
  ThreatCardContainer,
  LoaderThreatTable,
  SelectedButton,
} from './Style/Home.style';
import {
  Button,
  SearchBar,
  StatusMenu,
  ConfirmationModal,
  Loader,
} from '../../components';
import {
  BulkThreatDataType,
  ThreatTypes,
  selectedThreatDataType,
  UserData,
} from '../../helpers/types';
import CreateThreatModel from './CreateThreatModel';
import { PaginationStyle } from '../ThreadsDetails/Style/SingleThreat.style';
import AddCommonNotesModal from './AddCommonNotesModal';
import {
  useThreatStatusesQuery,
  useServicesQuery,
  useSourcesQuery,
} from '../../queries/lookups';
import ThreatTable from '../../components/ThreatTable/ThreatTable';
import BulkTakedownModal, {
  BulkTakedownItem,
} from '../../components/BulkTakedownModal/BulkTakedownModal';
import { useHomeThreatSocket } from '../../hooks/useHomeThreatSocket';
import { useSuppressedThreatKeys } from '../../hooks/useSuppressedThreatKeys';
import ScrollToTopButton from '../../components/ScrollToTopButton/ScrollToTopButton';
import useFilterUrlSync from '../../hooks/useFilterUrlSync';
import { parseSortFromParams } from '../../hooks/urlParamUtils';
import {
  setBrpConfidenceFilter,
  setBrpVerdictFilter,
  setCompanyFilter,
  setExcludeCompany,
  setProtectedAssetsExclude,
  setProtectedAssetsInclude,
  setPickerEndDate,
  setPickerStartDate,
  setReviewStatusFilterValues,
  setServicesData,
  setSourcesData,
  setStatusFilterValues,
} from '../../store/filter/action';
import {
  CompanyFilterMode,
  resolveCompanyFilterMode,
} from './companyFilterMode';
import { showToast } from '../../store/toaster/action';
import { exportThreatsToCsv } from '../../utils/threatExportUtils';

const THREAT_EXPORT_PAGE_SIZE = 1000;
const THREAT_EXPORT_MAX_PAGES = 20;

const Home = () => {
  const [openConfirmModal, setOpenConfirmModal] = useState<boolean>(false);
  const [openExternalPendingWarning, setOpenExternalPendingWarning] =
    useState<boolean>(false);
  const [selectedThreats, setSelectedThreats] = useState<any>([]); // TODO:
  const [openStatusMenu, setOpenStatusMenu] = useState<null | HTMLElement>(
    null,
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [companyFilterValues, setCompanyFilterValues] = useState<string[]>([]);
  const [searchValue, setSearchValue] = useState<string>('');
  const [selectedBulkStatusLabel, setSelectedBulkStatusLabel] =
    useState<string>('');
  const [openAddThreatModal, setOpenAddThreatModal] = useState<boolean>(false);
  const [bulkTakedownItems, setBulkTakedownItems] = useState<
    BulkTakedownItem[]
  >([]);
  const [bulkTakedownSkipped, setBulkTakedownSkipped] = useState<number>(0);
  const [hasMoreThreats, setHasMoreThreats] = useState<boolean>(true);
  const [networkData, setNetworkData] = useState<any>({});
  // Threat-list page size. Local (not Redux/persisted) — the former
  // threats.pageSizePersistValues slice cell was never whitelisted, so its
  // value already reset to PER_PAGE_RECORDS on every reload. WHEEL-341d.
  const [pageSizePersistValues, setPageSizePersistValues] =
    useState<number>(PER_PAGE_RECORDS);
  const [openCommonThreatModal, setOpenCommonThreatModal] =
    useState<boolean>(false);
  const [sortConfig, setSortConfig] = useState<{
    sortBy: string;
    sortOrder: string;
  }>(() => {
    const parsed = parseSortFromParams(
      new URLSearchParams(window.location.search),
    );
    return parsed ?? { sortBy: defaultSortBy, sortOrder: defaultSortOrder };
  });
  const [companyFilterMode, setCompanyFilterMode] =
    useState<CompanyFilterMode>('include');
  // When on, every selected company (include and exclude) is expanded to
  // itself + all its descendant companies before hitting the threats API
  // (WHEEL-376). Local state like companyFilterMode; shareable via the
  // `includeChildren` URL param (useFilterUrlSync).
  const [includeChildCompanies, setIncludeChildCompanies] = useState(false);
  const [assetFilterMode, setAssetFilterMode] =
    useState<CompanyFilterMode>('include');
  // Intentionally local state (not Redux) so it always resets to 'active_only'
  // on page load — filterList is redux-persisted and would survive reloads.
  const [protectedDomainMode, setProtectedDomainMode] = useState<
    'active_only' | 'all'
  >('active_only');
  const [openSortBy, setOpenSortBy] = useState<null | HTMLElement>(null);
  const [bypassFilters, setBypassFilters] = useState<boolean>(false);
  const [bypassSearching, setBypassSearching] = useState<boolean>(false);
  const [bypassProgressText, setBypassProgressText] = useState<string>('');
  const [isExportingThreats, setIsExportingThreats] = useState<boolean>(false);
  const [exportProgress, setExportProgress] = useState<number | null>(null);
  const bypassAbortRef = useRef<AbortController | null>(null);
  const isInitialMount = useRef(true);
  const isSortChangeRef = useRef(false);
  const hasSetDefaultSource = useRef(false);
  const pendingDescendantFetchRef = useRef(false);
  const pendingDescendantActionRef = useRef<
    'apply' | 'search' | 'clear' | null
  >(null);
  const dispatch = useDispatch();
  const location = useLocation();
  const queryClient = useQueryClient();

  // Snapshot of the filters actually applied to the list query. Unlike the
  // live filter state (updated on every keystroke/dropdown change), this only
  // changes at explicit trigger points (mount, Apply, Reset, search
  // submit/clear, sort, protected-domain toggle, page/page-size change) —
  // matching the former imperative getThreatAction dispatch behaviour where
  // typing in a filter field alone never refetched. WHEEL-341d.
  const [listParams, setListParams] = useState<ThreatListParams | null>(null);

  const {
    data: threatListData,
    isLoading: threatListLoading,
    isFetching: threatListFetching,
    refetch: refetchThreatList,
  } = useThreatListQuery(listParams);

  // Adapter preserving the shape the render/handlers read off the former
  // Redux `threatList` cell (`items`, `total`, `loading`, `isFetching`).
  const threats = useMemo(
    () => ({
      items: threatListData?.items ?? [],
      total: threatListData?.total ?? 0,
      loading: threatListLoading,
      isFetching: threatListFetching,
    }),
    [threatListData, threatListLoading, threatListFetching],
  );

  const { mutate: bulkChangeStatus } = useBulkChangeStatusMutation();

  const { data: currentUser, isLoading: currentUserLoading } =
    useCurrentUserQuery();
  // Compatibility shape for prop-drilled children typed against UserData
  const userData = {
    loading: currentUserLoading,
    data: currentUser,
  } as UserData;
  const pendingOperations = useSelector(
    (state: any) => state.bulkOperations.pendingOperations,
  );
  const suppressedThreatKeys = useSuppressedThreatKeys();
  const {
    companyFilterData,
    companysExclude,
    protectedAssetsInclude = [],
    protectedAssetsExclude = [],
    getServicesData,
    statusFilterData,
    getSourcesData,
    getPickerStartDate,
    getPickerEndDate,
    reviewStatusFilterData,
    // Default to [] — these keys are absent from older redux-persist state.
    brpVerdictFilterData = [],
    brpConfidenceFilterData = [],
  } = useSelector((state: any) => state.filterList);

  const { data: threatStatuses } = useThreatStatusesQuery();
  const { data: serviceData } = useServicesQuery();
  const { data: sourcesList } = useSourcesQuery();

  const selectedCompanyLabels = getLabelsAndIds(companyFilterData, 'id');
  const selectedStatusLabels = getLabelsAndIds(statusFilterData, 'label');
  const selectedReviewStatusLabels = getLabelsAndIds(
    reviewStatusFilterData,
    'label',
  );
  const selectedServiceLabels = getLabelsAndIds(getServicesData, 'label');
  const selectedSourceLabels = getLabelsAndIds(getSourcesData, 'value');
  const selectedBrpVerdictLabels = getLabelsAndIds(
    brpVerdictFilterData,
    'value',
  );
  const selectedBrpConfidenceLabels = getLabelsAndIds(
    brpConfidenceFilterData,
    'value',
  );
  const selectedCompanyExcludeLabels = getLabelsAndIds(companysExclude, 'id');

  // WHEEL-376: resolve descendant companies of every selected company (both
  // include and exclude lists) so a parent company can stand in for its whole
  // tenant tree. Fetches start as soon as companies are picked (not on Apply)
  // and are cached 5 min per company.
  const descendantLookupIds = useMemo(
    () =>
      Array.from(
        new Set(
          [...companyFilterData, ...companysExclude].map(
            (company: { id: number }) => company.id,
          ),
        ),
      ),
    [companyFilterData, companysExclude],
  );
  const { descendantsByCompanyId, isResolvingDescendants } =
    useCompanyDescendantIds(descendantLookupIds, includeChildCompanies);
  const expandedCompanyIds = includeChildCompanies
    ? expandWithDescendants(selectedCompanyLabels, descendantsByCompanyId)
    : selectedCompanyLabels;
  const expandedExcludeCompanyIds = includeChildCompanies
    ? expandWithDescendants(
        selectedCompanyExcludeLabels,
        descendantsByCompanyId,
      )
    : selectedCompanyExcludeLabels;
  const addedChildCompaniesCount =
    companyFilterMode === 'include'
      ? expandedCompanyIds.length - selectedCompanyLabels.length
      : expandedExcludeCompanyIds.length - selectedCompanyExcludeLabels.length;

  const getThreatActionProps = {
    currentPage,
    searchValue,
    selectedCompanyLabels,
    selectedStatusLabels,
  };

  const isCustomerReviewPage =
    location.pathname === '/threats/customer-review-requests';

  const { isInitializedFromUrl } = useFilterUrlSync({
    isCustomerReviewPage,
    sortConfig,
    setSortConfig,
    includeChildCompanies,
    setIncludeChildCompanies,
  });

  // Pending command-palette actions deeplinked via URL params (WHEEL-174).
  // Captured into a ref because useFilterUrlSync rewrites the URL (stripping
  // unknown params) as soon as filters are serialized back to the URL.
  const paletteActionRef = useRef<{ search?: string; createThreat?: boolean }>(
    {},
  );

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const paletteSearch = params.get('threat_search');
    if (paletteSearch) {
      paletteActionRef.current.search = paletteSearch;
    }
    if (params.get('create_threat') === 'true') {
      paletteActionRef.current.createThreat = true;
    }
  }, [location.search]);

  useEffect(() => {
    setCompanyFilterMode(currentMode =>
      resolveCompanyFilterMode(
        currentMode,
        companyFilterData.length,
        companysExclude.length,
      ),
    );
  }, [companyFilterData.length, companysExclude.length]);

  useEffect(() => {
    setAssetFilterMode(currentMode =>
      resolveCompanyFilterMode(
        currentMode,
        protectedAssetsInclude.length,
        protectedAssetsExclude.length,
      ),
    );
  }, [protectedAssetsInclude.length, protectedAssetsExclude.length]);

  // Persist a compact queue snapshot for the detail page's prev/next
  // navigation (WHEEL-328 cockpit). localStorage — not redux — because
  // analysts open details in new tabs (window.open), which don't inherit
  // the in-memory store.
  useEffect(() => {
    const items = threats?.items;
    if (!Array.isArray(items) || items.length === 0) return;
    try {
      localStorage.setItem(
        'threat-queue',
        JSON.stringify(
          items.map((item: any) => ({
            threat_id: item.threat_id,
            protected_asset_id: item.protected_asset_id,
            company_id: item.company_id,
            threat_name: item.threat_name,
            source: item.source,
          })),
        ),
      );
    } catch {
      // quota/serialization failures just mean no prev/next on the detail page
    }
  }, [threats?.items]);

  useEffect(() => {
    return () => {
      if (bypassAbortRef.current) {
        bypassAbortRef.current.abort();
      }
    };
  }, []);

  // `fetchThreats` is intentionally not part of this dependency list because its
  // identity changes on each render and would retrigger the page-load fetch loop.
  useEffect(() => {
    // Wait for useFilterUrlSync to parse URL → Redux before the first fetch,
    // so landing with filter params (e.g. Historical Scan Quick View) fires
    // one request using URL-derived filters instead of defaults. WHEEL-270.
    if (!isInitializedFromUrl) return;

    if (isCustomerReviewPage) {
      dispatch(setReviewStatusFilterValues(reviewStatusFilterData));
    } else if (location.pathname === '/dashboard') {
      dispatch(setStatusFilterValues(statusFilterData));
    }
    // Skip fetchThreats if the page change was caused by a sort change
    // (it will be called in the sortConfig useEffect instead)
    if (isSortChangeRef.current) {
      isSortChangeRef.current = false;
      return;
    }
    // Skip the default fetch when a palette bypass search is pending — the
    // effect below (which runs after this one) takes over the results.
    if (paletteActionRef.current.search) {
      return;
    }
    // Deep link with includeChildren=1: descendant resolution hasn't finished
    // yet, so firing now would query the parent ids only. Defer to the
    // descendant-settle effect below. WHEEL-376.
    if (includeChildCompanies && isResolvingDescendants) {
      pendingDescendantFetchRef.current = true;
      return;
    }
    fetchThreats(sortConfig);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, currentPage, isInitializedFromUrl]);

  // Replays actions deferred while descendant companies were still resolving:
  // the initial deep-linked fetch, and any Apply/search submitted during the
  // resolution window (they would otherwise fire with unexpanded ids). The
  // handler refs are assigned below on every render, so the replay sees the
  // post-resolution state, not the click-time closure.
  useEffect(() => {
    if (isResolvingDescendants) return;
    if (pendingDescendantFetchRef.current) {
      pendingDescendantFetchRef.current = false;
      fetchThreats(sortConfig);
    }
    const pendingAction = pendingDescendantActionRef.current;
    if (pendingAction) {
      pendingDescendantActionRef.current = null;
      if (pendingAction === 'apply') applyFilterRef.current();
      else if (pendingAction === 'search') searchSubmitRef.current();
      else if (pendingAction === 'clear') clearSearchRef.current();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isResolvingDescendants]);

  // Execute the pending palette action once URL → Redux init is done, so the
  // bypass search has status/service reference data available. Defined after
  // the page-load fetch effect so that effect can still observe the pending
  // search and skip its default fetch before we consume it here.
  useEffect(() => {
    if (!isInitializedFromUrl) return;
    const pending = paletteActionRef.current;
    if (pending.search) {
      const paletteQuery = pending.search;
      paletteActionRef.current = {};
      setSearchValue(paletteQuery);
      setBypassFilters(true);
      executeBypassSearch(paletteQuery);
    } else if (pending.createThreat) {
      paletteActionRef.current = {};
      handleAddThreatModal();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInitializedFromUrl, location.search]);

  // Home's two WebSocket subscriptions (threat_status_update +
  // asset_network_data) live in this headless hook (WHEEL-341d). It
  // reconciles bulk operations + the list query cache and streams network
  // enrichment into the local `networkData` state Home owns.
  useHomeThreatSocket({ threatItems: threats.items, setNetworkData });

  // Once every threat in a bulk batch has been confirmed, refetch the page
  // once. Until then the only list data we have is the response fetched
  // 500ms after the POST, which the backend served before applying the change:
  // its rows are filtered out by suppression, but its `total` was taken at face
  // value and put the header count back up - the count churn in SUP-3225.
  // After the last event the backend is authoritative for both. WHEEL-359.
  const awaitingBatchSettleRef = useRef(false);
  const activePendingCount = useMemo(
    () =>
      Object.values(pendingOperations).filter((op: any) => !op?.hasError)
        .length,
    [pendingOperations],
  );

  useEffect(() => {
    if (!awaitingBatchSettleRef.current) return;
    if (activePendingCount > 0) return;
    awaitingBatchSettleRef.current = false;
    refetchThreatList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePendingCount]);

  useEffect(() => {
    if (!threats?.loading && threats?.items?.length > 0) {
      setNetworkData((prevNetworkData: any) => {
        const updatedNetworkData = { ...prevNetworkData };

        threats?.items?.forEach((item: { threat_id: string | number }) => {
          if (!updatedNetworkData[item?.threat_id]) {
            updatedNetworkData[item?.threat_id] = {
              ssl: null,
              whois: null,
              ssl_loading: true,
              whois_loading: true,
            };
          }
        });

        return updatedNetworkData;
      });
    }
  }, [threats]);

  const onClickRefreshNetworkData = (threat_id: number) => {
    setNetworkData((prevState: any) => ({
      ...prevState,
      [threat_id]: {
        ...prevState[threat_id],
        whois_loading: true,
        ssl_loading: true,
        ssl: null,
        whois: null,
      },
    }));
  };

  // TanStack Query serves cached data instead of refetching when a query key
  // resolves to content it already holds fresh (e.g. re-applying the same
  // filters). Each explicit trigger (mount/Apply/Reset/search/sort/page)
  // must still reach the API even when the computed params are unchanged, so
  // force a refetch in that one case instead of just updating state — the
  // byte-identical-params guard, mirroring TakedownList (WHEEL-341b).
  const applyListParams = (next: ThreatListParams) => {
    if (listParams && JSON.stringify(listParams) === JSON.stringify(next)) {
      refetchThreatList();
    } else {
      setListParams(next);
    }
  };

  const fetchThreats = (
    sortConfigToUse: {
      sortBy: string;
      sortOrder: string;
    } = sortConfig,
    size = pageSizePersistValues ?? PER_PAGE_RECORDS,
    page = currentPage,
  ) => {
    applyListParams(buildThreatQueryParams({ page, size, sortConfigToUse }));
    setSelectedThreats([]);
  };

  const buildThreatQueryParams = useCallback(
    ({
      page = currentPage,
      size = pageSizePersistValues ?? PER_PAGE_RECORDS,
      sortConfigToUse = sortConfig,
    }: {
      page?: number;
      size?: number;
      sortConfigToUse?: {
        sortBy: string;
        sortOrder: string;
      };
    } = {}) => {
      const status = isCustomerReviewPage
        ? selectedReviewStatusLabels
        : selectedStatusLabels;

      return {
        from: getDateOrDefault(getPickerStartDate, defaultStartDate),
        until: getDateOrDefault(getPickerEndDate, defaultEndDate),
        status,
        threat_name: searchValue.trim(),
        company_ids: expandedCompanyIds,
        service: selectedServiceLabels,
        sort: sortConfigToUse.sortBy,
        order: sortConfigToUse.sortOrder,
        page,
        size,
        sources: selectedSourceLabels,
        brp_verdict: selectedBrpVerdictLabels,
        brp_confidence: selectedBrpConfidenceLabels,
        excludeCompanysId: expandedExcludeCompanyIds,
        protectedDomainMode,
        protected_asset_names: protectedAssetsInclude,
        exclude_protected_asset_names: protectedAssetsExclude,
      };
    },
    [
      currentPage,
      getPickerEndDate,
      getPickerStartDate,
      isCustomerReviewPage,
      pageSizePersistValues,
      searchValue,
      selectedBrpConfidenceLabels,
      selectedBrpVerdictLabels,
      expandedExcludeCompanyIds,
      expandedCompanyIds,
      selectedReviewStatusLabels,
      selectedServiceLabels,
      selectedSourceLabels,
      selectedStatusLabels,
      sortConfig,
      protectedDomainMode,
      protectedAssetsInclude,
      protectedAssetsExclude,
    ],
  );

  const handleExportThreats = useCallback(async () => {
    setIsExportingThreats(true);
    setExportProgress(0);

    try {
      const exportedThreats: ThreatTypes[] = [];
      let expectedTotal: number | null = null;

      for (let page = 1; page <= THREAT_EXPORT_MAX_PAGES; page += 1) {
        const response = await getThreatApi(
          buildThreatQueryParams({
            page,
            size: THREAT_EXPORT_PAGE_SIZE,
          }),
        );
        const data = response?.data;
        const items = Array.isArray(data?.items) ? data.items : [];

        if (typeof data?.total === 'number') {
          expectedTotal = data.total;
        }

        exportedThreats.push(...items);

        if (typeof expectedTotal === 'number' && expectedTotal > 0) {
          const expectedPages = Math.min(
            THREAT_EXPORT_MAX_PAGES,
            Math.ceil(expectedTotal / THREAT_EXPORT_PAGE_SIZE),
          );
          setExportProgress(Math.round((page / expectedPages) * 100));
        }

        const reachedExpectedTotal =
          typeof expectedTotal === 'number' &&
          exportedThreats.length >= expectedTotal;

        if (items.length < THREAT_EXPORT_PAGE_SIZE || reachedExpectedTotal) {
          break;
        }
      }

      exportThreatsToCsv(exportedThreats);
    } catch (error: any) {
      dispatch(
        showToast(
          error?.response?.data?.detail?.[0]?.msg ||
            error?.response?.data?.detail ||
            'Export failed. Please try again.',
          'error',
        ),
      );
    } finally {
      setIsExportingThreats(false);
      setExportProgress(null);
    }
  }, [buildThreatQueryParams, dispatch]);

  useEffect(() => {
    // Set "Daily Scan" as default after API loads (only once on initial load)
    if (
      !hasSetDefaultSource.current &&
      sourcesList &&
      sourcesList.length > 0 &&
      getSourcesData.length === 0
    ) {
      const dailyScanSource = sourcesList.find(
        source => source.name === 'daily_scan',
      );

      if (dailyScanSource) {
        dispatch(
          setSourcesData([
            {
              label: 'Daily Scan',
              value: 'daily_scan',
            },
          ]),
        );
      }
      hasSetDefaultSource.current = true;
    }
  }, [dispatch, sourcesList, getSourcesData.length]);

  const handleSortBy = (
    order: string | undefined,
    sortBy: string | undefined,
  ) => {
    if (order && sortBy) {
      setSortConfig({ sortBy, sortOrder: order });
    }
    setOpenSortBy(null);
  };

  // Fetch threats when sort or the protected-domains toggle changes (skip initial mount)
  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    // Reset to first page when sort changes
    isSortChangeRef.current = true;
    setCurrentPage(1);
    fetchThreats(sortConfig, pageSizePersistValues ?? PER_PAGE_RECORDS, 1);
  }, [pageSizePersistValues, sortConfig, protectedDomainMode]);
  /* eslint-enable react-hooks/exhaustive-deps */

  const handleUpdatedASCE = () => handleSortBy('ASCENDING', 'updated_at');
  const handleUpdatedDESC = () => handleSortBy('DESCENDING', 'updated_at');
  const handleDetectedASCE = () => handleSortBy('ASCENDING', 'detected_at');
  const handleDetectedDESC = () => handleSortBy('DESCENDING', 'detected_at');
  const handleDomainASCE = () =>
    handleSortBy('ASCENDING', 'protected_asset_name');
  const handleDomainDESC = () =>
    handleSortBy('DESCENDING', 'protected_asset_name');
  const handleCompanyGroup = () => handleSortBy('ASCENDING', 'company_id');
  // BRP classifier sort (ENG-718 / REQ-4). sortBy values map to the
  // brp_classification fields supported by threat-service (ENG-716).
  const handleBrpVerdictASCE = () => handleSortBy('ASCENDING', 'brp_verdict');
  const handleBrpVerdictDESC = () => handleSortBy('DESCENDING', 'brp_verdict');
  const handleBrpConfidenceASCE = () =>
    handleSortBy('ASCENDING', 'brp_confidence');
  const handleBrpConfidenceDESC = () =>
    handleSortBy('DESCENDING', 'brp_confidence');
  const handleBrpScoreASCE = () => handleSortBy('ASCENDING', 'brp_score');
  const handleBrpScoreDESC = () => handleSortBy('DESCENDING', 'brp_score');

  const handleApplyFilter = () => {
    // Children still resolving: queue the apply; the descendant-settle effect
    // replays it so the request carries the expanded ids. WHEEL-376.
    if (includeChildCompanies && isResolvingDescendants) {
      pendingDescendantActionRef.current = 'apply';
      return;
    }
    setCurrentPage(1);
    setHasMoreThreats(true);
    setSelectedThreats([]);
    applyListParams({
      from: getDateOrDefault(getPickerStartDate, defaultStartDate),
      until: getDateOrDefault(getPickerEndDate, defaultEndDate),
      status: isCustomerReviewPage
        ? selectedReviewStatusLabels
        : selectedStatusLabels,
      threat_name: searchValue.trim(),
      company_ids: expandedCompanyIds,
      service: selectedServiceLabels,
      sort: defaultSortBy,
      order: defaultSortOrder,
      page: 1,
      size: pageSizePersistValues || PER_PAGE_RECORDS,
      sources: selectedSourceLabels,
      brp_verdict: selectedBrpVerdictLabels,
      brp_confidence: selectedBrpConfidenceLabels,
      excludeCompanysId: expandedExcludeCompanyIds,
      protectedDomainMode,
      protected_asset_names: protectedAssetsInclude,
      exclude_protected_asset_names: protectedAssetsExclude,
    });
  };
  const handleResetFilter = () => {
    const defaultStatus = isCustomerReviewPage
      ? defaultReviewStatusFilter
      : defaultStatusFilter;

    if (isCustomerReviewPage) {
      dispatch(setReviewStatusFilterValues(defaultReviewStatusFilter));
    } else if (location.pathname === '/dashboard') {
      dispatch(setStatusFilterValues(defaultStatusFilter));
    }

    dispatch(setPickerStartDate(defaultStartDate));
    dispatch(setPickerEndDate(defaultEndDate));
    dispatch(setCompanyFilter([]));
    dispatch(setExcludeCompany([]));
    dispatch(setProtectedAssetsInclude([]));
    dispatch(setProtectedAssetsExclude([]));
    setCompanyFilterMode('include');
    setAssetFilterMode('include');
    setIncludeChildCompanies(false);
    setProtectedDomainMode('active_only');
    setSelectedThreats([]);
    setSearchValue('');
    setCompanyFilterValues([]);
    dispatch(setServicesData(defaultServiceFilter));
    dispatch(setSourcesData(defaultSourcesFilter));
    dispatch(setBrpVerdictFilter([]));
    dispatch(setBrpConfidenceFilter([]));
    setCurrentPage(1);
    applyListParams({
      from: getDateOrDefault(defaultStartDate, getPickerStartDate),
      until: getDateOrDefault(defaultEndDate, getPickerEndDate),
      status: defaultStatus?.map(status => status?.label),
      company_ids: 0,
      threat_name: '',
      service: defaultServiceFilter?.map(service => service?.label),
      sort: defaultSortBy,
      order: defaultSortOrder,
      page: 1,
      size: pageSizePersistValues || PER_PAGE_RECORDS,
      sources: defaultSourcesFilter?.map(sources => sources.value),
      brp_verdict: [],
      brp_confidence: [],
    });
  };

  // Shift+R resets the filters to their defaults and applies them right away.
  useHotkeys(
    'shift+r',
    () => {
      if (isTypingContext()) return;
      handleResetFilter();
    },
    { enableOnFormTags: false },
    [handleResetFilter],
  );

  // Cmd/Ctrl+Enter applies the current filters, including from inside filter
  // inputs. Blur first so half-typed autocomplete text (e.g. an asset name)
  // is committed to redux, then apply through a ref so the call sees the
  // post-commit state instead of this render's stale closure.
  const applyFilterRef = useRef(handleApplyFilter);
  applyFilterRef.current = handleApplyFilter;
  useHotkeys(
    'mod+enter',
    () => {
      if (document.querySelector('[role="dialog"]')) return;
      (document.activeElement as HTMLElement | null)?.blur?.();
      setTimeout(() => applyFilterRef.current(), 0);
    },
    { enableOnFormTags: true, preventDefault: true },
    [],
  );

  const handleSelectAll = useCallback(() => {
    console.log('Current threats:', threats?.items);
    console.log('Current pendingOperations:', pendingOperations);

    const visibleThreats = [
      // Get errored threats from pendingOperations
      ...Object.values(pendingOperations)
        .filter((op: any) => {
          if (!op.hasError || !op.originalData) {
            console.log('Filtered out pending operation:', op);
            return false;
          }
          return true;
        })
        .map((op: any) => op.originalData),
      // Get regular threats that aren't already shown as errored
      ...(threats?.items || []).filter((item: any) => {
        if (!item) {
          console.log('Filtered out null/undefined threat item');
          return false;
        }
        const key = bulkOperationKeyForThreat(item);
        if (pendingOperations?.[key]?.hasError) {
          console.log('Filtered out threat with pending error:', item);
          return false;
        }
        // Rows hidden by an in-flight bulk action must not be selectable, or
        // Select All re-selects threats the analyst already actioned and they
        // get actioned twice. WHEEL-359.
        if (suppressedThreatKeys.has(key)) return false;
        return true;
      }),
    ];

    if (selectedThreats?.length === visibleThreats.length) {
      setSelectedThreats([]);
    } else {
      const newSelectedThreats = visibleThreats
        .filter(
          (threat: ThreatTypes) =>
            // Add validation to ensure all required properties exist
            threat?.protected_asset_name &&
            threat?.threat_name &&
            threat?.threat_id &&
            threat?.company_id &&
            threat?.protected_asset_id &&
            threat?.identification_status?.value,
        )
        .map((threat: ThreatTypes) => ({
          domain: threat.protected_asset_name,
          threat: threat.threat_name,
          threatId: threat.threat_id,
          companyId: threat.company_id,
          domainId: threat.protected_asset_id,
          currentStatus: threat.identification_status.value,
        }));
      setSelectedThreats(newSelectedThreats || []);
    }
  }, [
    selectedThreats?.length,
    threats?.items,
    pendingOperations,
    suppressedThreatKeys,
  ]);

  const onChangeSatatuses = (
    e: React.SyntheticEvent,
    value: { label: string }[],
  ) => {
    if (isCustomerReviewPage) {
      dispatch(setReviewStatusFilterValues(value));
    } else if (location.pathname === '/dashboard') {
      dispatch(setStatusFilterValues(value));
    }
  };

  const onChangeCompany = (
    e: React.SyntheticEvent,
    value: { label: string; id: number }[],
  ) => {
    dispatch(setCompanyFilter(value));
  };

  const onChangeExcludeCompany = (
    e: React.SyntheticEvent,
    value: { label: string; id: number }[],
  ) => {
    dispatch(setExcludeCompany(value));
  };

  const handleToggleCompanyMode = (newMode: 'include' | 'exclude') => {
    if (newMode !== companyFilterMode) {
      setCompanyFilterMode(newMode);
    }
  };

  const onChangeAssetsInclude = (names: string[]) => {
    dispatch(setProtectedAssetsInclude(names));
  };

  const onChangeAssetsExclude = (names: string[]) => {
    dispatch(setProtectedAssetsExclude(names));
  };

  const handleToggleAssetMode = (newMode: 'include' | 'exclude') => {
    if (newMode !== assetFilterMode) {
      setAssetFilterMode(newMode);
    }
  };

  // Changing the mode triggers an immediate refetch via the sort/filter
  // useEffect above (protectedDomainMode is in its dependency list).
  const handleToggleProtectedDomainMode = (newMode: 'active_only' | 'all') => {
    if (newMode !== protectedDomainMode) {
      setProtectedDomainMode(newMode);
    }
  };

  const onChangeService = (
    e: React.SyntheticEvent,
    value: { label: string }[],
  ) => {
    dispatch(setServicesData(value));
  };

  const onChangeSource = (
    e: React.SyntheticEvent,
    value: { label: string; value: string }[],
  ) => {
    dispatch(setSourcesData(value));
  };

  const onChangeBrpVerdict = (
    e: React.SyntheticEvent,
    value: { label: string; value: string }[],
  ) => {
    dispatch(setBrpVerdictFilter(value));
  };

  const onChangeBrpConfidence = (
    e: React.SyntheticEvent,
    value: { label: string; value: string }[],
  ) => {
    dispatch(setBrpConfidenceFilter(value));
  };

  const onChangeDate = (value: any) => {
    if (value) {
      const [start, end] = value;
      const formattedStartDate = start || '';
      const formattedEndDate = end || '';

      dispatch(setPickerStartDate(formattedStartDate));
      dispatch(setPickerEndDate(formattedEndDate));
    } else {
      dispatch(setPickerStartDate(''));
      dispatch(setPickerEndDate(''));
    }
  };

  const handleOpenStatusMenu = (event: React.MouseEvent<HTMLElement>) => {
    setOpenStatusMenu(event.currentTarget);
  };

  const handleCloseStatusMenu = () => {
    setOpenStatusMenu(null);
  };

  const handleConfirmModal = (value: string) => {
    setSelectedBulkStatusLabel(value);
    setOpenStatusMenu(null);

    if (isRejectionStatus(value)) {
      const hasExternalPending = selectedThreats.some(
        (item: any) =>
          item.status === EXTERNAL_THREAT_PENDING_STATUS ||
          item.currentStatus === EXTERNAL_THREAT_PENDING_STATUS,
      );
      if (hasExternalPending) {
        setOpenExternalPendingWarning(true);
        return;
      }
    }

    setOpenConfirmModal(true);
  };

  const handleRemoveExternalPendingAndContinue = () => {
    const filtered = selectedThreats.filter(
      (item: any) =>
        item.status !== EXTERNAL_THREAT_PENDING_STATUS &&
        item.currentStatus !== EXTERNAL_THREAT_PENDING_STATUS,
    );
    setSelectedThreats(filtered);
    setOpenExternalPendingWarning(false);
    if (filtered.length > 0) {
      setOpenConfirmModal(true);
    }
  };

  const updateBulkThreatsStatus = () => {
    console.log('Starting bulk update with threats:', selectedThreats);

    setOpenConfirmModal(false);

    // Create bulk operations records
    const bulkOperations = selectedThreats.map(
      (threat: {
        threatId: number;
        threat: string;
        companyId: number;
        domainId: number;
        status: string;
      }) => ({
        threatId: threat.threatId,
        threatName: threat.threat,
        startedAt: new Date().toISOString(),
        expectedStatus: selectedBulkStatusLabel,
        companyId: threat.companyId,
        domainId: threat.domainId,
        // Match on company + asset + threat, not threat_id alone: the same
        // threat appears once per company/asset pair, so an id-only lookup
        // stored the wrong sibling's row here. On failure that row is what gets
        // pinned to the top, so the analyst saw a duplicate of one sibling and
        // never saw the one that actually failed. WHEEL-359.
        originalData: threats.items.find(
          (item: {
            threat_id: number;
            company_id: number;
            protected_asset_id: number;
          }) =>
            item.threat_id === threat.threatId &&
            item.company_id === threat.companyId &&
            item.protected_asset_id === threat.domainId,
        ),
      }),
    );

    console.log('Created bulk operations:', bulkOperations);

    // First add the operations to state
    awaitingBatchSettleRef.current = true;
    dispatch(addBulkOperation(bulkOperations));

    // Wait 500ms before hiding threats and fetching new ones
    setTimeout(() => {
      console.log('Dispatching bulk status change with items:', items);
      bulkChangeStatus({ items });

      // Move on to the next page with a fresh set of threats. Analysts clear a
      // page and advance, and want the leftovers left behind (confirmed with
      // PreCrime Labs on WHEEL-359), so this behaviour stays.
      const nextPage = currentPage < count ? currentPage + 1 : 1;

      if (nextPage === currentPage) {
        // Single page, or wrapped back to page 1: setCurrentPage would be a
        // no-op and the [currentPage] effect would not fire, so fetch here.
        fetchThreats(sortConfig, pageSizePersistValues, nextPage);
      } else {
        // setCurrentPage alone triggers the [currentPage] effect, which
        // fetches. Calling fetchThreats here as well fired a second,
        // byte-identical request through the applyListParams guard. WHEEL-359.
        setCurrentPage(nextPage);
      }
    }, 500);
    const items = selectedThreats.map((item: BulkThreatDataType) => ({
      threat_name: item.threat,
      threatId: item.threatId,
      status: selectedBulkStatusLabel,
      user_id: userData?.data?.Id,
      company_id: item.companyId,
      process: 'w',
      priority: 1,
      protected_asset_id: item.domainId,
      domain: item.domain,
      currentStatus: item.status,
    }));

    setSelectedThreats([]);
  };

  // Bulk takedown initiation (WHEEL-195). Only confirmed threats are
  // eligible; row selections carry the status as `status`, select-all as
  // `currentStatus`.
  const selectedConfirmedThreats = useMemo(
    () =>
      selectedThreats.filter(
        (item: any) => (item.status ?? item.currentStatus) === 'confirmed',
      ),
    [selectedThreats],
  );

  const handleOpenBulkTakedownModal = () => {
    setBulkTakedownSkipped(
      selectedThreats.length - selectedConfirmedThreats.length,
    );
    // The list can show the same threat once per company, but a takedown is
    // keyed by (threat, protected asset): dedupe so the item count matches
    // the per-item results the backend will send (it dedupes server-side).
    const uniqueItems = new Map<string, BulkTakedownItem>();
    selectedConfirmedThreats.forEach(
      (item: { threatId: number; domainId: number; threat: string }) => {
        uniqueItems.set(`${item.threatId}_${item.domainId}`, {
          threatId: item.threatId,
          protectedAssetId: item.domainId,
          threatName: item.threat,
        });
      },
    );
    setBulkTakedownItems(Array.from(uniqueItems.values()));
  };

  const handleBulkTakedownComplete = useCallback(() => {
    setSelectedThreats([]);
  }, []);

  const handleChangeSelectedThreats = useCallback(
    (threatsItem: selectedThreatDataType) => {
      setSelectedThreats((prevThreats: any[]) => {
        const indexOf = prevThreats.findIndex(
          (threat: { threatId: number; domainId: number; companyId: number }) =>
            threat.threatId === threatsItem.threatId &&
            threat.domainId === threatsItem.domainId &&
            threat.companyId === threatsItem.companyId,
        );
        if (indexOf > -1) {
          const newThreats = [...prevThreats];
          newThreats.splice(indexOf, 1);
          return newThreats;
        }
        return [...prevThreats, threatsItem];
      });
    },
    [],
  );

  const handleChangeSearch = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
  };

  const generateYearRanges = (): Array<{
    from: string;
    until: string;
    label: string;
  }> => {
    const ranges: Array<{ from: string; until: string; label: string }> = [];
    const startYear = 2021;
    const currentYear = new Date().getFullYear();
    const today = dayjs().endOf('day').format('YYYY-MM-DD HH:mm:ss');

    for (let year = currentYear; year >= startYear; year--) {
      const from = `${year}-01-01 00:00:00`;
      const until = year === currentYear ? today : `${year}-12-31 23:59:59`;
      ranges.push({ from, until, label: `${year}` });
    }
    return ranges;
  };

  const EXCLUDED_STATUSES = ['detected-by-logdetection'];

  const getAllFilterValues = () => {
    const allStatuses: string[] = (() => {
      if (threatStatuses && threatStatuses.length > 0) {
        return threatStatuses
          .map(s => s.name)
          .filter((name: string) => !EXCLUDED_STATUSES.includes(name));
      }
      return statusList
        .map((s: { Name: string }) => s.Name)
        .filter((name: string) => !EXCLUDED_STATUSES.includes(name));
    })();

    const allServices: string[] = serviceData
      ? serviceData.map(s => s.name)
      : [];

    const storeSources = sourcesList;
    const allSources: string[] = storeSources
      ? storeSources.map(s => s.name)
      : SourcesData.map(s => s.value);

    return { allStatuses, allServices, allSources };
  };

  const cancelBypassSearch = () => {
    if (bypassAbortRef.current) {
      bypassAbortRef.current.abort();
      bypassAbortRef.current = null;
    }
    setBypassSearching(false);
    setBypassProgressText('');
  };

  const executeBypassSearch = async (queryOverride?: string) => {
    const bypassQuery = (queryOverride ?? searchValue).trim();
    if (bypassAbortRef.current) {
      bypassAbortRef.current.abort();
    }

    const abortController = new AbortController();
    bypassAbortRef.current = abortController;

    setBypassSearching(true);
    setBypassProgressText('Preparing search...');
    setSelectedThreats([]);
    setCurrentPage(1);

    // The table reads whatever `listParams` key is currently mounted. Seed it
    // (if a search hasn't been applied yet, e.g. a palette deeplink triggers
    // bypass before the initial fetch effect runs) so the accumulated results
    // below have a query-cache entry to land in — mirrors TakedownList.
    const activeListParams = listParams ?? buildThreatQueryParams();
    if (!listParams) {
      setListParams(activeListParams);
    }
    queryClient.setQueryData(threatListQueryKey(activeListParams), {
      items: [],
      total: 0,
    });

    const yearRanges = generateYearRanges();
    const { allStatuses, allServices, allSources } = getAllFilterValues();
    const primarySort = sortConfig;

    let accumulatedItems: any[] = [];
    let totalFound = 0;

    try {
      for (const range of yearRanges) {
        if (abortController.signal.aborted) {
          break;
        }

        setBypassProgressText(
          totalFound > 0
            ? `Found ${totalFound} result(s) — searching ${range.label}...`
            : `Searching ${range.label}...`,
        );

        const params = {
          from: range.from,
          until: range.until,
          status: allStatuses,
          threat_name: bypassQuery,
          company_ids: [],
          service: allServices,
          sort: primarySort.sortBy,
          order: primarySort.sortOrder,
          page: 1,
          size: pageSizePersistValues || PER_PAGE_RECORDS,
          sources: allSources,
          excludeCompanysId: [],
        };

        try {
          const response = await getThreatApi(params, abortController.signal);
          const data = response?.data;

          if (abortController.signal.aborted) {
            break;
          }

          if (data?.items && data.items.length > 0) {
            accumulatedItems = [...accumulatedItems, ...data.items];
            totalFound += data.items.length;

            queryClient.setQueryData(threatListQueryKey(activeListParams), {
              items: accumulatedItems,
              total: totalFound,
            });
          }
        } catch (error: any) {
          if (
            error?.name === 'CanceledError' ||
            error?.code === 'ERR_CANCELED' ||
            abortController.signal.aborted
          ) {
            break;
          }
          console.error(`Bypass search error for ${range.label}:`, error);
          continue;
        }
      }

      if (!abortController.signal.aborted) {
        if (totalFound > 0) {
          setBypassProgressText(`Found ${totalFound} result(s) total`);
        } else {
          setBypassProgressText('No results found');
        }
      }
    } finally {
      setBypassSearching(false);
      bypassAbortRef.current = null;
    }
  };

  const handleToggleBypass = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.checked;
    setBypassFilters(newValue);
    if (!newValue && bypassSearching) {
      cancelBypassSearch();
    }
    setBypassProgressText('');
  };

  const handleSearchSubmit = () => {
    if (bypassFilters) {
      executeBypassSearch();
      return;
    }
    // Same deferral as handleApplyFilter — search uses the expanded company
    // ids too. WHEEL-376.
    if (includeChildCompanies && isResolvingDescendants) {
      pendingDescendantActionRef.current = 'search';
      return;
    }
    setCurrentPage(1);
    applyListParams({
      from: getDateOrDefault(getPickerStartDate, defaultStartDate),
      until: getDateOrDefault(getPickerEndDate, defaultEndDate),
      status: isCustomerReviewPage
        ? selectedReviewStatusLabels
        : selectedStatusLabels,
      threat_name: searchValue.trim(),
      company_ids: expandedCompanyIds,
      service: selectedServiceLabels,
      sort: sortConfig.sortBy,
      order: sortConfig.sortOrder,
      page: 1,
      size: pageSizePersistValues || PER_PAGE_RECORDS,
      sources: selectedSourceLabels,
      brp_verdict: selectedBrpVerdictLabels,
      brp_confidence: selectedBrpConfidenceLabels,
      excludeCompanysId: expandedExcludeCompanyIds,
      protectedDomainMode,
      protected_asset_names: protectedAssetsInclude,
      exclude_protected_asset_names: protectedAssetsExclude,
    });
    setSelectedThreats([]);
  };

  const handleAddThreatModal = () => {
    setOpenAddThreatModal(true);
  };

  const handleClearSearch = () => {
    // Same deferral as handleApplyFilter. WHEEL-376.
    if (includeChildCompanies && isResolvingDescendants) {
      pendingDescendantActionRef.current = 'clear';
      return;
    }
    if (bypassSearching) {
      cancelBypassSearch();
    }
    setBypassProgressText('');
    applyListParams({
      from: getDateOrDefault(getPickerStartDate, defaultStartDate),
      until: getDateOrDefault(getPickerEndDate, defaultEndDate),
      status: isCustomerReviewPage
        ? selectedReviewStatusLabels
        : selectedStatusLabels,
      threat_name: '',
      company_ids: expandedCompanyIds,
      service: selectedServiceLabels,
      sort: sortConfig.sortBy,
      order: sortConfig.sortOrder,
      page: 1,
      size: pageSizePersistValues || PER_PAGE_RECORDS,
      sources: selectedSourceLabels,
      brp_verdict: selectedBrpVerdictLabels,
      brp_confidence: selectedBrpConfidenceLabels,
      excludeCompanysId: expandedExcludeCompanyIds,
      protectedDomainMode,
      protected_asset_names: protectedAssetsInclude,
      exclude_protected_asset_names: protectedAssetsExclude,
    });
    setSearchValue('');
    setSelectedThreats([]);
  };

  // Latest-handler refs for the descendant-settle replay (WHEEL-376), same
  // pattern as applyFilterRef above.
  const searchSubmitRef = useRef(handleSearchSubmit);
  searchSubmitRef.current = handleSearchSubmit;
  const clearSearchRef = useRef(handleClearSearch);
  clearSearchRef.current = handleClearSearch;

  const count = Math.ceil(threats?.total / pageSizePersistValues);
  const handleChangePage = (event: any, value: number) => {
    setCurrentPage(value);
    setSelectedThreats([]);
  };

  const handlePageSizeChange = (event: SelectChangeEvent<string>) => {
    const selectedPageSize = Number(event?.target?.value);
    setCurrentPage(1);
    // Updating pageSize triggers the sort/pageSize effect, which resets to
    // page 1 and refetches — no direct fetch needed here (the former direct
    // fetchThreats + setPageSize dispatch double-fired against the API).
    setPageSizePersistValues(selectedPageSize);
  };

  const isThreatSelected = useMemo(() => {
    return (threatData: selectedThreatDataType) => {
      const { companyId, domainId, threatId } = threatData;
      const indexOf = selectedThreats.findIndex(
        (threat: selectedThreatDataType) =>
          threat.threatId === threatId &&
          threat.domainId === domainId &&
          threat.companyId === companyId,
      );
      return indexOf > -1;
    };
  }, [selectedThreats]);

  return (
    <Box sx={{ padding: '24px' }}>
      <FilterCard
        onChangeSatatuses={onChangeSatatuses}
        onChangeCompany={onChangeCompany}
        onChangeService={onChangeService}
        onChangeDate={onChangeDate}
        handleApplyFilter={handleApplyFilter}
        handleResetFilter={handleResetFilter}
        statuesFilterValues={selectedStatusLabels}
        companyFilterValues={companyFilterValues}
        getServicesData={getServicesData}
        getPickerStartDate={getPickerStartDate}
        getPickerEndDate={getPickerEndDate}
        openSortBy={openSortBy}
        setOpenSortBy={setOpenSortBy}
        handleUpdatedASCE={handleUpdatedASCE}
        handleUpdatedDESC={handleUpdatedDESC}
        handleDetectedASCE={handleDetectedASCE}
        handleDetectedDESC={handleDetectedDESC}
        handleDomainASCE={handleDomainASCE}
        handleDomainDESC={handleDomainDESC}
        handleCompanyGroup={handleCompanyGroup}
        handleBrpVerdictASCE={handleBrpVerdictASCE}
        handleBrpVerdictDESC={handleBrpVerdictDESC}
        handleBrpConfidenceASCE={handleBrpConfidenceASCE}
        handleBrpConfidenceDESC={handleBrpConfidenceDESC}
        handleBrpScoreASCE={handleBrpScoreASCE}
        handleBrpScoreDESC={handleBrpScoreDESC}
        getSourcesData={getSourcesData}
        onChangeSource={onChangeSource}
        onChangeBrpVerdict={onChangeBrpVerdict}
        onChangeBrpConfidence={onChangeBrpConfidence}
        getBrpVerdictData={brpVerdictFilterData}
        getBrpConfidenceData={brpConfidenceFilterData}
        onChangeExcludeCompany={onChangeExcludeCompany}
        isCustomerReviewPage={isCustomerReviewPage}
        sortConfig={sortConfig}
        companyFilterMode={companyFilterMode}
        assetFilterMode={assetFilterMode}
        onToggleAssetMode={handleToggleAssetMode}
        onChangeAssetsInclude={onChangeAssetsInclude}
        onChangeAssetsExclude={onChangeAssetsExclude}
        onToggleCompanyMode={handleToggleCompanyMode}
        includeChildCompanies={includeChildCompanies}
        onToggleIncludeChildCompanies={setIncludeChildCompanies}
        isResolvingChildCompanies={isResolvingDescendants}
        addedChildCompaniesCount={addedChildCompaniesCount}
        protectedDomainMode={protectedDomainMode}
        onToggleProtectedDomainMode={handleToggleProtectedDomainMode}
      />
      <Box sx={{ width: '100%', mt: 1.5 }}>
        <Grid
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            rowGap: 1,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <ThreatsCountLabel data-testid="threats-total-count">
              {threats?.loading || bypassSearching ? (
                <Loader size={16} />
              ) : (
                threats?.total?.toLocaleString()
              )}{' '}
              Threats
            </ThreatsCountLabel>
            <SearchBar
              data-testid="threat-search-bar"
              size="small"
              handleChange={handleChangeSearch}
              handleSearch={handleSearchSubmit}
              searchValue={searchValue}
              handleClearSearch={handleClearSearch}
              bypassFilters={bypassFilters}
              onToggleBypass={handleToggleBypass}
              bypassSearching={bypassSearching}
              bypassProgressText={bypassProgressText}
              onCancelBypassSearch={cancelBypassSearch}
            />
          </Box>
          <Box
            sx={{
              display: 'flex',
              gap: '8px',
              alignItems: 'center',
              flexWrap: 'wrap',
            }}
            id="bulk"
          >
            <SelectedButton
              data-testid={`${
                threats?.items?.length > 0 &&
                selectedThreats?.length === threats?.items?.length
                  ? 'clear-selected-threats-button'
                  : 'select-all-threats-button'
              }`}
              variant="text"
              size="medium"
              label={
                threats?.items?.length > 0 &&
                selectedThreats?.length === threats?.items?.length
                  ? `Clear selected(${selectedThreats?.length})`
                  : `Select all${
                      selectedThreats?.length
                        ? `(${selectedThreats.length})`
                        : ''
                    }`
              }
              disabled={threats.loading || !threats?.items?.length}
              onClick={handleSelectAll}
            />
            {!isCustomerReviewPage && (
              <Button
                data-testid="export-threats-csv-button"
                variant="outlined"
                disabled={
                  threats.loading ||
                  threats.isFetching ||
                  bypassSearching ||
                  isExportingThreats
                }
                startIcon={
                  isExportingThreats ? (
                    <CircularProgress color="inherit" size={14} />
                  ) : undefined
                }
                onClick={handleExportThreats}
                label={
                  isExportingThreats
                    ? exportProgress != null
                      ? `Exporting... ${exportProgress}%`
                      : 'Exporting...'
                    : 'Export CSV'
                }
              />
            )}
            <Button
              data-testid="add-threat-button"
              variant="contained"
              onClick={handleAddThreatModal}
            >
              + Add Threat
            </Button>
            {selectedThreats?.length > 0 && (
              <>
                <Button
                  data-testid="change-threats-status-button"
                  variant="contained"
                  size="medium"
                  label="Change Status"
                  onClick={handleOpenStatusMenu}
                />
                <Button
                  data-testid="add-threats-notes-button"
                  variant="outlined"
                  size="medium"
                  label="Add Notes"
                  onClick={() => setOpenCommonThreatModal(true)}
                />
                <Button
                  data-testid="threats-bulk-takedown-start"
                  variant="outlined"
                  size="medium"
                  label="Initiate Takedowns"
                  disabled={selectedConfirmedThreats.length === 0}
                  onClick={handleOpenBulkTakedownModal}
                />
              </>
            )}
          </Box>
        </Grid>
      </Box>

      <Box>
        {/* Table view - always shown (card view soft-deleted) */}
        <ThreatCardContainer data-testid="threats-grid-container">
          {threats?.loading || threats?.isFetching || bypassSearching ? (
            <LoaderThreatTable>
              <Loader size={25} />
            </LoaderThreatTable>
          ) : !threats?.items?.length &&
            !threats.loading &&
            !threats.isFetching ? (
            <ThreatCardWrapper>No Data Found</ThreatCardWrapper>
          ) : (
            <ThreatTable
              threats={threats}
              handleChangeSelectedThreats={handleChangeSelectedThreats}
              isThreatSelected={isThreatSelected}
              getThreatActionProps={getThreatActionProps}
              threatNetworkData={networkData}
              userData={userData}
              onSelectAll={handleSelectAll}
              onDeselectAll={() => setSelectedThreats([])}
              currentPage={currentPage}
              totalPages={count || 0}
              onPageChange={(page: number) => {
                setCurrentPage(page);
                setSelectedThreats([]);
              }}
              isModalOpen={
                openConfirmModal ||
                openAddThreatModal ||
                openCommonThreatModal ||
                openExternalPendingWarning
              }
              onBulkStatusChange={() => {
                const btn = document.querySelector<HTMLButtonElement>(
                  '[data-testid="change-threats-status-button"]',
                );
                btn?.click();
              }}
              hasSelectedThreats={selectedThreats?.length > 0}
            />
          )}
          <PaginationStyle style={{ marginTop: '16px' }}>
            <Select
              data-testid="threats-page-size-select"
              value={String(pageSizePersistValues)}
              onChange={handlePageSizeChange}
              label="Size"
              fullWidth
              style={{ width: '80px', height: '35px', marginLeft: '20px' }}
            >
              <MenuItem value="10">10</MenuItem>{' '}
              <MenuItem value="25">25</MenuItem>
              <MenuItem value="50">50</MenuItem>
              <MenuItem value="100">100</MenuItem>
            </Select>
            <Pagination
              data-testid="threats-pagination"
              count={count || 0}
              page={currentPage}
              onChange={handleChangePage}
              color="primary"
            />
          </PaginationStyle>
        </ThreatCardContainer>
        <ScrollToTopButton />
      </Box>

      <CreateThreatModel
        open={openAddThreatModal}
        handleClose={() => setOpenAddThreatModal(false)}
        userData={userData}
      />
      <AddCommonNotesModal
        open={openCommonThreatModal}
        handleClose={() => setOpenCommonThreatModal(false)}
        selectedThreats={selectedThreats}
      />
      <StatusMenu
        items={threatsStatus}
        openStatusMenu={openStatusMenu}
        closeStatusMenu={handleCloseStatusMenu}
        handleOnClick={handleConfirmModal}
      />
      {bulkTakedownItems.length > 0 && (
        <BulkTakedownModal
          items={bulkTakedownItems}
          skippedCount={bulkTakedownSkipped}
          onClose={() => setBulkTakedownItems([])}
          onComplete={handleBulkTakedownComplete}
        />
      )}
      <ConfirmationModal
        title="Confirm status"
        subTitle="Do you want to change the status?"
        confirmLabel="Update"
        open={openConfirmModal}
        handleClose={() => setOpenConfirmModal(false)}
        handleUpdate={() => updateBulkThreatsStatus()}
      />
      <Modal
        open={openExternalPendingWarning}
        onClose={() => setOpenExternalPendingWarning(false)}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            bgcolor: 'background.paper',
            border: 'none !important',
            borderRadius: '10px',
            boxShadow: 24,
            p: 4,
          }}
          style={{ width: 400 }}
        >
          <Typography variant="subtitle1" fontWeight="bold">
            External Threat Pending
          </Typography>
          <Typography sx={{ mt: 1 }}>
            Some of the selected threats have 'external-threat-pending' status
            and cannot be bulk{' '}
            {selectedBulkStatusLabel === 'benign'
              ? 'marked as benign'
              : 'discarded'}
            . You can remove them from the selection and continue with the rest.
          </Typography>
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              alignItems: 'center',
              justifyContent: 'center',
              mt: 2,
            }}
          >
            <Button
              onClick={() => setOpenExternalPendingWarning(false)}
              variant="outlined"
              label="Cancel"
            />
            <Button
              variant="contained"
              onClick={handleRemoveExternalPendingAndContinue}
              label="Remove & Continue"
            />
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default Home;
